
import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * GB SEARCH — technical code index integration.
 * The JSON asset is generated from ACTIVE products available on Online Store.
 * It is read only on technical-code searches, and is never written by this script.
 */
let gbCodeIndexPromise = null;

const gbNormalizeCode = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

const gbIsTechnicalQuery = (value) => {
  const text = value.trim();
  if (text.length < 3) return false;
  return /\d/.test(text) || (/[A-Za-zÀ-ÿ]/.test(text) && text === text.toUpperCase());
};

const gbLoadIndex = async () => {
  if (!gbCodeIndexPromise) {
    // Resolve beside this theme JS asset, avoiding a hard-coded theme ID or CDN URL.
    const indexUrl = new URL('./gb-search-codes.json', import.meta.url);
    gbCodeIndexPromise = fetch(indexUrl, { cache: 'force-cache' })
      .then((response) => {
        if (!response.ok) throw new Error(`GB search index HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (data?.schema !== 'gb-search-codes/v1' || !Array.isArray(data.products)) {
          throw new Error('Invalid GB search index schema');
        }
        return data.products;
      })
      .catch((error) => {
        // Nonblocking failure: keep native/custom Shopify results intact.
        console.warn('[GB Search] Technical index unavailable:', error);
        gbCodeIndexPromise = null;
        return [];
      });
  }
  return gbCodeIndexPromise;
};

const gbFindCodeMatches = (products, searchTerm, limit = 4) => {
  const query = gbNormalizeCode(searchTerm);
  if (query.length < 3) return [];

  const candidates = [];
  const seenUrls = new Set();

  for (const item of products) {
    if (!Array.isArray(item) || item.length < 5) continue;
    const [title, brand, path, image, codes] = item;
    if (typeof path !== 'string' || !/^\/products\/[a-z0-9][a-z0-9-]*$/i.test(path)) continue;
    if (!Array.isArray(codes) || !codes.length || seenUrls.has(path)) continue;

    let bestScore = 99;
    let matchingCode = '';
    for (const code of codes) {
      if (typeof code !== 'string' || !code.trim() || code.trim() === '—') continue;
      const normalized = gbNormalizeCode(code);
      let score = 99;
      if (normalized === query) score = 0;
      else if (normalized.startsWith(query)) score = 1;
      else if (query.length >= 4 && normalized.includes(query)) score = 2;

      if (score < bestScore) {
        bestScore = score;
        matchingCode = code;
      }
    }
    if (bestScore < 99) {
      seenUrls.add(path);
      candidates.push({
        title: String(title || ''),
        brand: String(brand || ''),
        path,
        image: typeof image === 'string' ? image : '',
        code: matchingCode,
        score: bestScore,
      });
    }
  }

  candidates.sort((a, b) => a.score - b.score || a.code.localeCompare(b.code));
  return candidates.slice(0, limit);
};

const gbSearchCodeLabels = {
  it: 'Codici prodotto',
  en: 'Product codes',
  fr: 'Codes produit',
  de: 'Produktcodes',
};

function gbEnsureCodeStyles() {
  if (document.getElementById('gb-search-code-styles')) return;
  const style = document.createElement('style');
  style.id = 'gb-search-code-styles';
  style.textContent = `
    .gb-search-codes { padding: 12px 20px 22px; box-sizing: border-box; }
    .gb-search-codes__title { font-size: 15px; font-weight: 500; margin: 0 0 12px; }
    .gb-search-codes__grid { display: grid !important; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; padding: 0 !important; margin: 0 !important; list-style: none; }
    .gb-search-codes__item { width: auto !important; min-width: 0; }
    .gb-search-codes__link { display: block; color: inherit; text-decoration: none; min-width: 0; }
    .gb-search-codes__image { display: block; aspect-ratio: 4 / 5; width: 100%; object-fit: cover; background: #f5f5f5; }
    .gb-search-codes__code { display: block; font-size: 12px; font-weight: 600; margin-top: 7px; overflow-wrap: anywhere; }
    .gb-search-codes__name { display: block; font-size: 12px; line-height: 1.4; margin-top: 3px; overflow-wrap: anywhere; }
    .gb-search-codes__brand { display: block; font-size: 11px; opacity: .65; margin-top: 2px; }
    @media (max-width: 749px) {
      .gb-search-codes { padding-inline: 14px; }
      .gb-search-codes__grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
    }
  `;
  document.head.appendChild(style);
}

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;
      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;
      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : 0;
        break;
      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Inject the technical-code results after the Shopify section has been updated.
   * It does not interfere with native products or GB Collection results.
   * @param {string} searchTerm
   * @param {AbortSignal} signal
   */
  async #addTechnicalCodeResults(searchTerm, signal) {
    if (!gbIsTechnicalQuery(searchTerm)) return;

    const products = await gbLoadIndex();
    if (signal.aborted || !this.isConnected || this.refs.searchInput.value.trim() !== searchTerm) return;
    const matches = gbFindCodeMatches(products, searchTerm, 4);
    if (!matches.length) return;

    const root = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
    if (!root) return;

    gbEnsureCodeStyles();

    const section = document.createElement('section');
    section.className = 'gb-search-codes';
    section.setAttribute(
      'aria-label',
      gbSearchCodeLabels[(document.documentElement.lang || 'it').split('-')[0]] || gbSearchCodeLabels.it
    );

    const heading = document.createElement('h4');
    heading.className = 'gb-search-codes__title';
    heading.textContent = section.getAttribute('aria-label');
    section.appendChild(heading);

    const list = document.createElement('ul');
    list.className = 'gb-search-codes__grid predictive-search-results__list predictive-search-results__wrapper-products';
    const localizedPrefix = new URL(Theme.routes.search_url, location.origin).pathname
      .replace(/\/search\/?$/, '')
      .replace(/\/$/, '');

    for (const match of matches) {
      // Do not duplicate native result cards already rendered by Shopify.
      const href = `${localizedPrefix}${match.path}`;
      const alreadyThere = Array.from(root.querySelectorAll('a[href]')).some((el) => {
        try {
          return new URL(el.href).pathname === href;
        } catch {
          return false;
        }
      });
      if (alreadyThere) continue;

      const li = document.createElement('li');
      li.className = 'gb-search-codes__item predictive-search-results__card predictive-search-results__card--product';
      li.setAttribute('ref', 'resultsItems[]');

      const a = document.createElement('a');
      a.href = href;
      a.className = 'gb-search-codes__link';

      if (/^https:\/\/cdn\.shopify\.com\//i.test(match.image)) {
        const image = document.createElement('img');
        image.src = match.image;
        image.alt = match.title;
        image.loading = 'lazy';
        image.decoding = 'async';
        image.className = 'gb-search-codes__image';
        a.appendChild(image);
      }

      for (const [className, value] of [
        ['gb-search-codes__code', match.code],
        ['gb-search-codes__name', match.title],
        ['gb-search-codes__brand', match.brand],
      ]) {
        const span = document.createElement('span');
        span.className = className;
        span.textContent = value;
        a.appendChild(span);
      }

      li.appendChild(a);
      list.appendChild(li);
    }

    if (!list.children.length) return;
    section.appendChild(list);

    // Put code matches ahead of other product cards, after collection links.
    const firstProductSection = root.querySelector('#predictive-search-products');
    if (firstProductSection) firstProductSection.before(section);
    else {
      const otherGroup = root.querySelector('.predictive-search-results__wrapper-gb-collections');
      if (otherGroup) otherGroup.after(section);
      else root.prepend(section);
    }

    // A code hit supersedes Shopify's generic 'no results' message.
    root.querySelector('.predictive-search-results__no-results')?.remove();
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    // GB CUSTOM — extends predictive search to tag/body and Shopify default fields.
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag,body');

    const { predictiveSearchResults } = this.refs;
    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup || abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);
        this.#resetScrollPositions();

        // Add code matches asynchronously without blocking existing results.
        this.#addTechnicalCodeResults(searchTerm, abortController.signal).catch((error) => {
          if (!abortController.signal.aborted) {
            console.warn('[GB Search] Code result rendering failed:', error);
          }
        });
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;
    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;
    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
